/**
 * #582 AD-05 — return a child's screen to the picker when its acting profile ends.
 *
 * The server sets a sliding 60 s expiry. `toChildActor` anchors `expiresAt - serverNow`
 * to the request's SEND on both local clocks, charging the entire round trip. Slow responses
 * therefore shorten the displayed lifetime; they never add their transit time. The local stamp
 * is still named `receivedAt` for compatibility. The PIN, boot and renewal paths all record it.
 *
 * Monotonic elapsed time resists backward wall-clock steps. A one-second heartbeat and focus,
 * pageshow and any visibility change also check wall elapsed time, because some browsers pause
 * the monotonic clock during OS sleep. These checks never read or renew. A forward wall-clock
 * step can return a child early at ANY heartbeat, not only on wake. No browser callback runs
 * during suspension, and a simultaneous sleep plus backward wall step can fool both clocks;
 * these client clocks cannot prove absolute real time in that combination.
 *
 * A trusted keydown, pointerdown, touchstart or click owes a renewal read (d-20260928-14).
 * This is the operator console's WAKE set (`AuthContext.jsx:32`); its INPUT_EVENTS at line 31
 * also includes pointermove, which is not a tap here. Synthetic input never owes a read.
 * Reads use a 10 s window, one in flight, with a 1 s floor near expiry. A queued tap renews
 * when that window opens, so expiry is 60 s after the successful READ, not exactly the tap.
 * A failed read gets one retry only while both clocks still say live. At monotonic expiry,
 * wall-only expiry or during grace, failure returns immediately. Overdue queued work cannot read.
 *
 * A read already in flight has at most 5 s after the earlier local deadline to answer. Every
 * callback checks that absolute bound; a starved timer grants no extra grace. A wall-only-expired
 * trusted tap inside this bound may ask once; outside it the tap reads nothing. Expired inputs
 * are consumed before the control underneath can handle them. Nobody, another child, an expired
 * answer or a late answer cannot bring a child back. Effect cleanup invalidates the old flight.
 *
 * Return order: clear the child and show the picker immediately; deactivate on the server;
 * only after success, refresh the account if the actor generation still belongs to this return.
 * Failure or a newer actor means no account read. The ordinary actor carries no authority.
 * Deactivate itself is not conditional on an actor generation on the server (see record §8).
 *
 * #580 D1 (#903): a FAMILY TURN's member. The server never renews a turn on a read, so a tap's renewal
 * is the turn's own POST (`lib/familyInteraction.js`), by the same windows, and its answer keeps the
 * turn state beside the child. A saved completion or undo renews the turn too, and the screen reports
 * the later deadline here, as it reports a refusal that ended the turn. The return sends no deactivate:
 * the server ends the turn at its own deadline, and a refusal means it has ended, while a deactivate ends
 * whatever turn the session holds NOW, which another tab on the same cookie may have started. So it only
 * stops acting: the child goes, the turn reads locked (or revoked), the picker shows, and a reload does not
 * bring that generation back (`lib/familyTurnFence.js`). A retry resends the number its failed send drew. A first
 * send whose answer shows a higher number already kept gets one catch-up, while this effect still holds the turn on
 * screen inside its deadline; a retry gets none, as the higher number may have come after the server saved it. A
 * deadline learned is never moved back, and an effect a newer turn replaced, not yet cleaned up, changes nothing.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { deactivateProfile, fetchActingProfile, monotonicNow, toChildActor } from '../api/session.js';
import { nextRenewalSequence, renewFamilyTurn, subscribeFamilyTurn } from '../lib/familyInteraction.js';
import { retireFamilyGeneration } from '../lib/familyTurnFence.js';
import { DEFAULT_ROUTE } from '../routes/routeTable.js';
import { useActor } from './ActorContext.jsx';

/** Where the picker lives (`LoginRoute.jsx`). */
export const PICKER_ROUTE = DEFAULT_ROUTE;

/** At most one renewal read per this many milliseconds. */
export const RENEW_MIN_INTERVAL_MS = 10_000;

/** Never two renewal reads closer than this, even with less than one window left. */
export const RENEW_FLOOR_MS = 1_000;

/** How long past the deadline an in-flight renewal read may take to answer. */
export const RENEW_ANSWER_GRACE_MS = 5_000;

const TAP_EVENTS = ['keydown', 'pointerdown', 'touchstart', 'click'];
const WAKE_EVENTS = [[() => window, 'focus'], [() => window, 'pageshow'], [() => document, 'visibilitychange']];

/** Only a real person's input keeps a child on screen; an event a script made does not. */
export const inputPolicy = { isTrusted: (event) => event.isTrusted === true };

/** `owed`: the renewal number a retry resends. `latest`: the latest deadline learned for the turn, in ms. */
const freshRenewal = (generation) => ({ pending: false, retry: false, generation, owed: null, latest: -Infinity });

/**
 * #903: the renewal renewed nothing because the server already keeps a HIGHER number (another tab's, or this
 * page's clock stepped back). Keeping the number it was sent is the server's one-second floor, not this.
 */
const behind = (answer, sent) => answer?.renewed === false && Number(answer.familyTurn?.interactionSequence) > sent;

/**
 * @param {{endActor?: Function, readActor?: Function}} props `endActor` ends the actor on the server
 *   and `readActor` reads it; a test passes its own. The real session helpers are looked up only when
 *   a child acts, never while rendering, so a screen test that mocks the session module still renders.
 */
export default function ChildActorReturn({ endActor, readActor }) {
  const context = useActor();
  const navigate = useNavigate();
  const live = useRef(null);
  live.current = { ...context, navigate, endActor, readActor };
  const child = context.actor?.kind === 'child' ? context.actor : null;
  // Survives a renewal, which replaces the actor and so this effect: a tap made while a read was in
  // flight is still owed a read afterwards. #903: never by the next family turn.
  const renewal = useRef(freshRenewal(null));

  useEffect(() => {
    if (!child) {
      renewal.current = freshRenewal(null);
      return undefined;
    }
    if (renewal.current.generation !== (child.generation ?? null)) {
      renewal.current = freshRenewal(child.generation ?? null);
    }
    const r = renewal.current;
    const { familyTurn } = live.current;
    const turn = familyTurn?.state === 'active' && familyTurn.generation === child.generation ? familyTurn : null;
    const received = child.receivedAt ?? {};
    const lifetime = Number.isFinite(received?.mono) && Number.isFinite(received?.wall) ? child.lifetimeMs : -Infinity;
    /** Time left by each clock. NaN counts as none. */
    const left = () => ({
      mono: lifetime - (monotonicNow() - received.mono),
      wall: lifetime - (Date.now() - received.wall),
    });
    /** `over` by the monotonic clock; `unsure` when only the wall clock says so (a sleep or a step). */
    const state = () => {
      const l = left();
      if (!(l.mono > 0)) return 'over';
      return l.wall > 0 ? 'live' : 'unsure';
    };
    // The window starts at the request send, on the monotonic clock.
    let lastReadAt = Number.isFinite(received?.mono) ? received.mono : -Infinity;
    let flight = null;
    let over = false;
    let windowTimer = null;
    let graceTimer = null;
    /** #903: the context still shows THIS turn, as the member and as the turn. */
    const onScreen = () => live.current.actor?.generation === child.generation
      && live.current.familyTurn?.generation === child.generation;
    /** #903: the latest deadline learned for this turn, here or by an effect before this one. */
    const known = () => Math.max(r.latest, Date.parse(child.expiresAt));

    const returnToPicker = (ended = 'locked') => {
      if (over) return;
      over = true;
      // A newer turn is on screen and this effect is still to be cleaned up: that turn is not this one's to end.
      if (turn && !onScreen()) return;
      r.pending = false;
      r.retry = false;
      const { setActor, navigate: go, refresh, endActor: end } = live.current;
      if (turn) {
        retireFamilyGeneration(turn.generation);
        setActor({ actor: null, familyTurn: { state: ended, household: turn.household } });
        go(PICKER_ROUTE, { replace: true });
        return;
      }
      const clearedAt = setActor(null);
      go(PICKER_ROUTE, { replace: true });
      Promise.resolve()
        .then(() => (end ?? deactivateProfile)())
        .then(() => refresh({ ifEpoch: clearedAt }), () => {});
    };

    // Both clocks bound grace too. A delayed callback never starts a fresh five seconds.
    const graceLeft = () => Math.min(left().mono, left().wall) + RENEW_ANSWER_GRACE_MS;
    const atDeadline = () => {
      if (over) return;
      const remaining = graceLeft();
      if (!flight || !(remaining > 0)) { returnToPicker(); return; }
      if (!graceTimer) graceTimer = window.setTimeout(returnToPicker, remaining);
    };

    /** An owed read waits for the window, never past the deadline's last window, never under the floor. */
    const remember = () => {
      if (!r.pending || over || flight || windowTimer) return;
      const deadline = received.mono + lifetime;
      const at = Math.max(
        lastReadAt + RENEW_FLOOR_MS,
        Math.min(lastReadAt + RENEW_MIN_INTERVAL_MS, deadline - RENEW_MIN_INTERVAL_MS),
      );
      windowTimer = window.setTimeout(() => { windowTimer = null; renew(); }, Math.max(0, at - monotonicNow()));
    };

    const renew = (wallUncertainTap = false) => {
      if (over) return;
      // Queued work is not a new tap. It may have slept past the deadline.
      if (state() === 'over' || (state() === 'unsure' && !wallUncertainTap)) { atDeadline(); return; }
      if (wallUncertainTap && !(graceLeft() > 0)) { returnToPicker(); return; }
      if (flight) { r.pending = true; return; }
      const isRetry = r.retry;
      r.pending = false;
      r.retry = false;
      lastReadAt = monotonicNow();
      // #903: a retry resends the number its failed send drew, so a renewal the server saved is not paid twice.
      if (turn && !(isRetry && r.owed)) r.owed = { sequence: nextRenewalSequence(child.generation), kind: 'activate-control' };
      const { owed } = r;
      const read = turn
        ? () => renewFamilyTurn(child.generation, owed.kind, owed.sequence)
          .then((answer) => {
            // A retry's higher number may be another tab's after the server saved this one, so a first send only;
            // and only while this effect still holds the turn on screen, inside its deadline.
            if (isRetry || !behind(answer, owed.sequence) || over || !onScreen() || !(graceLeft() > 0)) return answer;
            // One catch-up above the number the server keeps, which a retry then resends.
            owed.sequence = nextRenewalSequence(child.generation);
            return renewFamilyTurn(child.generation, owed.kind, owed.sequence);
          })
          .then((answer) => answer.ended ?? answer.actingProfile)
        : live.current.readActor ?? fetchActingProfile;
      const requestedAt = { mono: monotonicNow(), wall: Date.now() };
      const mine = Promise.resolve().then(() => read());
      flight = mine;
      mine.then((acting) => {
        if (over || flight !== mine) return;
        if (!(graceLeft() > 0)) { returnToPicker(); return; }
        flight = null;
        if (typeof acting === 'string') { returnToPicker(acting); return; }
        const current = live.current.actor;
        const same = acting && current && String(acting.userId) === String(current.id)
          && (acting.generation ?? null) === (current.generation ?? null);
        const renewed = same ? toChildActor(acting, current, requestedAt) : null;
        // #903: a saved write's news may already have taught a later deadline; an earlier one is never applied.
        if (renewed && turn && Date.parse(acting.expiresAt) < known()) { remember(); return; }
        if (!renewed || !(renewed.lifetimeMs - Math.max(monotonicNow() - renewed.receivedAt.mono, Date.now() - renewed.receivedAt.wall) > 0)) { returnToPicker(); return; }
        if (turn) r.latest = Math.max(r.latest, Date.parse(acting.expiresAt));
        live.current.setActor(turn ? { actor: renewed, familyTurn: { ...turn, expiresAt: acting.expiresAt, serverNow: acting.serverNow } } : renewed);
        remember();
      }, () => {
        if (over || flight !== mine) return;
        flight = null;
        if (graceTimer || state() !== 'live') { returnToPicker(); return; }
        // One more try for a tap whose read failed; a new tap owes its own read anyway.
        if (!r.pending && !isRetry) { r.pending = true; r.retry = true; }
        remember();
      });
    };

    const onTap = (event) => {
      if (over || !inputPolicy.isTrusted(event)) return;
      const now = state();
      if (now !== 'live') { event.preventDefault(); event.stopPropagation(); }
      if (now === 'over') { atDeadline(); return; }
      r.pending = true;
      // #903: a new tap is a new interaction, so its renewal draws a new number, even in place of a retry, and is
      // owed its own retry.
      r.owed = null;
      if (turn) r.retry = false;
      // Only the wall clock says it is over: ask the server now, and let its answer decide.
      if (now === 'unsure') { renew(true); atDeadline(); return; }
      // An open window reads at once: `remember` schedules it for now.
      remember();
    };

    const check = () => { if (state() !== 'live') atDeadline(); };
    // An actor that arrives already over is returned here, in the first pass of effects, before any
    // timer. This is a passive effect, so that screen can still be painted for one frame (review L6):
    // accepted, because the view carries no authority and nothing it reads can renew the actor.
    check();
    if (over) return undefined;
    const timer = window.setTimeout(atDeadline, Math.max(0, left().mono));
    remember();
    const heartbeat = window.setInterval(check, 1000);
    for (const type of TAP_EVENTS) window.addEventListener(type, onTap, { capture: true, passive: false });
    for (const [target, type] of WAKE_EVENTS) target().addEventListener(type, check);
    // #903: news from a screen's write, for THIS turn only: an answer for an earlier turn changes nothing.
    const unsubscribe = turn && subscribeFamilyTurn((generation, news) => {
      if (over || generation !== child.generation || !onScreen()) return;
      if (news.ended) { returnToPicker(news.ended); return; }
      // A saved write renewed the turn: the same anchor, a later deadline. Never an earlier one, nor one that an
      // answer or a notice delivered before this render already passed.
      const expiresAt = Date.parse(news.expiresAt);
      if (!(expiresAt > known())) return;
      r.latest = expiresAt;
      const later = expiresAt - Date.parse(child.expiresAt);
      live.current.setActor({
        actor: { ...child, expiresAt: news.expiresAt, lifetimeMs: child.lifetimeMs + later },
        familyTurn: { ...turn, expiresAt: news.expiresAt },
      });
    });
    return () => {
      over = true;
      if (unsubscribe) unsubscribe();
      window.clearTimeout(timer);
      window.clearInterval(heartbeat);
      window.clearTimeout(windowTimer);
      window.clearTimeout(graceTimer);
      for (const type of TAP_EVENTS) window.removeEventListener(type, onTap, { capture: true });
      for (const [target, type] of WAKE_EVENTS) target().removeEventListener(type, check);
    };
  }, [child]);

  return null;
}
